import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaPlane, 
  FaCalendarAlt, 
  FaUsers, 
  FaArrowRight,
  FaCheck,
  FaExchangeAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaChild,
  FaBaby
} from 'react-icons/fa';

const FlightBooking = () => {
  const [tripType, setTripType] = useState('round-trip');
  const [formData, setFormData] = useState({
    tripType: 'round-trip',
    departureCity: '',
    arrivalCity: '',
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    classType: 'economy',
    specialRequests: '',
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const nigerianCities = [
    { code: 'LOS', name: 'Lagos', airport: 'Murtala Muhammed International Airport' },
    { code: 'ABV', name: 'Abuja', airport: 'Nnamdi Azikiwe International Airport' },
    { code: 'KAN', name: 'Kano', airport: 'Mallam Aminu Kano International Airport' },
    { code: 'PHC', name: 'Port Harcourt', airport: 'Port Harcourt International Airport' },
    { code: 'CBQ', name: 'Calabar', airport: 'Margaret Ekpo International Airport' },
    { code: 'ENU', name: 'Enugu', airport: 'Akanu Ibiam International Airport' },
    { code: 'IBA', name: 'Ibadan', airport: 'Ibadan Airport' },
    { code: 'JOS', name: 'Jos', airport: 'Yakubu Gowon Airport' },
    { code: 'MDI', name: 'Makurdi', airport: 'Makurdi Airport' },
    { code: 'YOL', name: 'Yola', airport: 'Yola Airport' }
  ];

  const classTypes = [
    { value: 'economy', label: 'Economy Class', description: 'Standard seating and services' },
    { value: 'business', label: 'Business Class', description: 'Premium seating' },
    { value: 'first', label: 'First Class', description: 'Luxury experience' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePassengerChange = (type, increment) => {
    setFormData({
      ...formData,
      [type]: Math.max(0, formData[type] + (increment ? 1 : -1))
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formDataToSend = {
      ...formData,
      _captcha: false,
      _template: "table",
      _autoresponse: "Thanks for your request! We'll respond shortly.",
      _token: "10d0b5f657f51e47f0b98f7f43832928", // Replace with your actual token
    };
  
    const encoded = new URLSearchParams(formDataToSend).toString();
  
    try {
      await fetch("https://formsubmit.co/10d0b5f657f51e47f0b98f7f43832928", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });
  
      // Show success message in UI
      setIsSubmitted(true);
  
      // Optionally clear the form
      // setFormData(initialState);
  
      // Hide after a few seconds if needed
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("FormSubmit failed:", error);
      alert("There was a problem submitting the form.");
    }
  };
  

  const swapCities = () => {
    setFormData({
      ...formData,
      departureCity: formData.arrivalCity,
      arrivalCity: formData.departureCity
    });
  };

  return (
    <div className="min-h-screen pt-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-green overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1558204692-5f402fe220b9')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaPlane className="text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold">Flight Booking</h1>
            </div>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Book domestic and international flights with ease. Compare prices, 
              choose your preferred class, and enjoy seamless travel across Nigeria and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <FaCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Flight Request Submitted!</h3>
              <p className="text-lg text-gray-600 mb-4">
                Thank you for choosing Yowa Travels for your flight booking.
              </p>
              <p className="text-green-600 font-semibold text-lg">
                An agent will be with you shortly to confirm your booking and provide the best available options!
              </p>
              <div className="mt-8 p-6 bg-green-50 rounded-2xl">
                <p className="text-sm text-green-700">
                  <strong>What's Next?</strong><br />
                  Our team will review your requirements and send you flight options within 1 hours during business hours.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              {/* Trip Type Selection */}
              <div className="p-8 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Select Trip Type</h3>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: 'round-trip', label: 'Round Trip', icon: FaExchangeAlt },
                    { value: 'one-way', label: 'One Way', icon: FaArrowRight },
                  ].map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => {
                          setTripType(type.value);
                          setFormData({ ...formData, tripType: type.value });
                        }}
                        className={`flex items-center space-x-3 px-6 py-4 rounded-xl border-2 transition-all duration-300 ${
                          tripType === type.value
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-green-300 text-gray-700'
                        }`}
                      >
                        <IconComponent className="text-lg" />
                        <span className="font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-8">

                {/* Flight Route Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-green-500" />
                    <span>Flight Route</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
                    <div>
                      <label className="form-label">Departure City *</label>
                      <select
                        name="departureCity"
                        value={formData.departureCity}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="">Select departure city</option>
                        {nigerianCities.map((city) => (
                          <option key={city.code} value={city.code}>
                            {city.name} ({city.code}) - {city.airport}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Swap Button */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 lg:block hidden">
                      <button
                        type="button"
                        onClick={swapCities}
                        className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
                      >
                        <FaExchangeAlt />
                      </button>
                    </div>

                    <div>
                      <label className="form-label">Arrival City *</label>
                      <select
                        name="arrivalCity"
                        value={formData.arrivalCity}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="">Select arrival city</option>
                        {nigerianCities.map((city) => (
                          <option key={city.code} value={city.code}>
                            {city.name} ({city.code}) - {city.airport}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Travel Dates */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaCalendarAlt className="text-green-500" />
                    <span>Travel Dates</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Departure Date *</label>
                      <input
                        type="date"
                        name="departureDate"
                        value={formData.departureDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    
                    {tripType === 'round-trip' && (
                      <div>
                        <label className="form-label">Return Date *</label>
                        <input
                          type="date"
                          name="returnDate"
                          value={formData.returnDate}
                          onChange={handleInputChange}
                          className="form-input"
                          min={formData.departureDate || new Date().toISOString().split('T')[0]}
                          required={tripType === 'round-trip'}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Passengers */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaUsers className="text-green-500" />
                    <span>Passengers</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { key: 'adults', label: 'Adults', description: '12+ years', icon: FaUser, min: 1 },
                      { key: 'children', label: 'Children', description: '2-11 years', icon: FaChild, min: 0 },
                      { key: 'infants', label: 'Infants', description: 'Under 2 years', icon: FaBaby, min: 0 }
                    ].map((passenger) => {
                      const IconComponent = passenger.icon;
                      return (
                        <div key={passenger.key} className="border border-gray-200 rounded-xl p-6">
                          <div className="flex items-center space-x-3 mb-4">
                            <IconComponent className="text-green-500 text-xl" />
                            <div>
                              <h5 className="font-bold text-gray-800">{passenger.label}</h5>
                              <p className="text-sm text-gray-600">{passenger.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange(passenger.key, false)}
                              disabled={formData[passenger.key] <= passenger.min}
                              className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                            >
                              -
                            </button>
                            <span className="text-xl font-bold text-gray-800 mx-4">
                              {formData[passenger.key]}
                            </span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange(passenger.key, true)}
                              className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Class Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Travel Class</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {classTypes.map((classType) => (
                      <label
                        key={classType.value}
                        className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                          formData.classType === classType.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="classType"
                          value={classType.value}
                          checked={formData.classType === classType.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <h5 className="font-bold text-gray-800 mb-2">{classType.label}</h5>
                          <p className="text-sm text-gray-600">{classType.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Special Requests
                <div className="mb-8">
                  <label className="form-label">Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="Any special dietary requirements, wheelchair assistance, seat preferences, or other requests..."
                  ></textarea>
                </div> */}

                {/* Contact Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Whatsapp Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+234 (0) 900 000 0000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-yowa-primary text-lg px-12 py-4 inline-flex items-center space-x-3"
                  >
                    <FaPlane />
                    <span>Search Flights</span>
                    <FaArrowRight />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    No payment required now. We'll send you the best flight options first.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Book Flights with Yowa Travels?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make flight booking simple, secure, and rewarding with exclusive benefits and professional support.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaCheck,
                title: 'Best Price Guarantee',
                description: 'We compare prices across all major airlines to ensure you get the best deal available.',
                color: 'bg-green-500'
              },
              {
                icon: FaClock,
                title: '24/7 Support',
                description: 'Round-the-clock assistance for flight changes, cancellations, and emergency support.',
                color: 'bg-blue-500'
              },
              {
                icon: FaPlane,
                title: 'Flexible Booking',
                description: 'Easy date changes, multiple payment options, and hassle-free booking modifications.',
                color: 'bg-purple-500'
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlightBooking;